import { useState } from "react";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { Calendar, ChevronLeft, ChevronRight, MessageSquare, MoreHorizontal, Phone } from "lucide-react";
import UpdateLeadModal from "./UpdateLeadModal";
import InteractionHistoryModal from "./InteractionHistoryModal";
import { Lead } from "../../types/crm";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";
import { getLocalStorageItem } from "../../utils/Validation";

interface LeadTableProps {
  leads: Lead[];
  onUpdateLeads: (leads: Lead) => void;
  total: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  loading: boolean;
  rmList: { id: number; name: string }[];
  statusOptions: { id: number; value: string }[];
  onLeadAdded?: () => void;
  onDeleteLeads: (id: number) => void;
}

const LeadTable = ({
  leads,
  onUpdateLeads,
  total,
  currentPage,
  pageSize,
  onPageChange,
  loading,
  rmList,
  statusOptions,
  onLeadAdded,
  onDeleteLeads,
}: LeadTableProps) => {
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const getStatusColor = (status: string) => {
    const colors = {
      New: "bg-sky-100 text-sky-700 border-sky-200",
      Contacted: "bg-amber-100 text-amber-700 border-amber-200",
      "Meeting Setup": "bg-emerald-100 text-emerald-700 border-emerald-200",
      Interested: "bg-blue-100 text-blue-700 border-blue-200",
      "Proposal Sent": "bg-amber-100 text-amber-700 border-amber-200",
      Won: "bg-emerald-100 text-emerald-700 border-emerald-200",
      Lost: "bg-rose-100 text-rose-700 border-rose-200",
      "Not Interested": "bg-slate-100 text-slate-700 border-slate-200",
      "Follow Up": "bg-blue-100 text-blue-700 border-blue-200",
      "Hot Lead": "bg-rose-100 text-rose-700 border-rose-200",
      "Warm Lead": "bg-orange-100 text-orange-700 border-orange-200",
    };
    return colors[status as keyof typeof colors] || "bg-slate-100 text-slate-700 border-slate-200";
  };

  const getRMNameById = (userId: number) => {
    const rm = rmList.find((entry) => entry.id === userId);
    return rm ? rm.name.trim() : "N/A";
  };

  const handleRowClick = (lead: Lead) => {
    setSelectedLead(lead);
    setShowUpdateModal(true);
  };

  const handleUpdate = (lead: Lead, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedLead(lead);
    setShowUpdateModal(true);
  };

  const handleDelete = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const userDataStr = getLocalStorageItem("detail");
      const userData = typeof userDataStr === "string" ? JSON.parse(userDataStr) : userDataStr;
      const userId = userData?.userId;
      if (!userId) return;

      const response = await apiService.delete(`/leads/${id}/${userId}`);
      onDeleteLeads(id);
      toast.info((response as any).message);
    } catch (error: any) {
      toast.error(error?.message || "Failed to delete lead");
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-GB");
  };

  const isFollowUpOverdue = (dateString: string) => new Date(dateString) < new Date();

  const getLastInteractionRemark = (lead: Lead) => {
    if (!lead.interactions || lead.interactions.length === 0) return "";
    return lead.interactions[0].remark;
  };

  const goToPage = (page: number) => onPageChange(Math.min(Math.max(page, 1), totalPages), pageSize);

  return (
    <TooltipProvider>
      <Card className="overflow-hidden rounded-[20px] border border-slate-200 bg-white shadow-[0_10px_20px_rgba(15,23,42,0.045)]">
        <div className="overflow-x-auto">
          <table className="min-w-full border-separate border-spacing-0 ">
            <thead>
              <tr className="bg-[#f8fafc]">
                <th className="whitespace-nowrap border-b border-slate-200 px-3.5 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                  Company Name
                </th>
                <th className="whitespace-nowrap border-b border-slate-200 px-3.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                  Designation
                </th>
                <th className="whitespace-nowrap border-b border-slate-200 px-3.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                  Name
                </th>
                <th className="whitespace-nowrap border-b border-slate-200 px-3.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                  Phone Number
                </th>
                <th className="whitespace-nowrap border-b border-slate-200 px-3.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                  Location
                </th>
                <th className="whitespace-nowrap border-b border-slate-200 px-3.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                  Lead RM
                </th>
                <th className="whitespace-nowrap border-b border-slate-200 px-3.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                  Total Calls
                </th>
                <th className="whitespace-nowrap border-b border-slate-200 px-3.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                  Next Follow-up
                </th>
                <th className="whitespace-nowrap border-b border-slate-200 px-3.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                  Last Interaction
                </th>
                <th className="whitespace-nowrap border-b border-slate-200 px-3.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                  Status
                </th>
                <th className="whitespace-nowrap border-b border-slate-200 px-3.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                  Created At
                </th>
                <th className="whitespace-nowrap border-b border-slate-200 px-3.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={12} className="px-3.5 py-7 text-center text-sm text-slate-500">
                      Loading...
                    </td>
                  </tr>
                ) : leads.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-3.5 py-7 text-center text-sm text-slate-500">
                      No leads found.
                    </td>
                  </tr>
              ) : (
                leads.map((lead, index) => {
                  const statusObj = statusOptions.find((opt) => opt.id === lead.leadStatusId);
                  const lastInteraction = getLastInteractionRemark(lead);
                  return (
                    <tr
                      key={lead.id}
                      className={`transition-colors hover:bg-slate-50 ${index !== leads.length - 1 ? "border-b border-slate-200" : ""}`}
                      onClick={() => handleRowClick(lead)}
                      onMouseEnter={() => setHoveredRow(lead.id)}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      <td className="max-w-[170px] px-3.5 py-2.5 align-top text-[12px] font-semibold text-slate-900">
                        <div className="leading-snug">{lead.companyName}</div>
                      </td>
                      <td className="max-w-[150px] px-3.5 py-2.5 align-top text-[12px] text-slate-600">
                        <div className="leading-snug">{lead.designation || "-"}</div>
                      </td>
                      <td className="max-w-[140px] px-3.5 py-2.5 align-top text-[12px] text-slate-900">
                        <div className="leading-snug">{lead.name}</div>
                      </td>
                      <td className="px-3.5 py-2.5 align-top text-[12px] text-slate-600">{lead.mobile || "-"}</td>
                      <td className="px-3.5 py-2.5 align-top text-[12px] text-slate-600">{lead.location || "-"}</td>
                      <td className="px-3.5 py-2.5 align-top text-[12px] text-slate-600">
                        {getRMNameById(lead.rmleadId || 0)}
                      </td>
                      <td className="px-3.5 py-2.5 align-top text-[12px] text-slate-900">
                        <div className="flex items-center gap-2">
                          <Phone className="h-3.5 w-3.5 text-slate-400" />
                          <span className="font-medium">{lead.totalCalls || lead.interactions?.length || 0}</span>
                        </div>
                      </td>
                      <td className="px-3.5 py-2.5 align-top text-[12px]">
                        {lead.nextFollowUp ? (
                          <div className="flex items-center gap-2">
                            <Calendar className={`h-3.5 w-3.5 ${isFollowUpOverdue(lead.nextFollowUp) ? "text-rose-500" : "text-slate-400"}`} />
                            <span className={`${isFollowUpOverdue(lead.nextFollowUp) ? "font-semibold text-rose-600" : "text-slate-600"}`}>
                              {formatDate(lead.nextFollowUp)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-3.5 py-2.5 align-top text-[12px] text-slate-600">
                        {lastInteraction ? (
                          <Tooltip open={hoveredRow === lead.id}>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-2">
                                <MessageSquare className="h-3.5 w-3.5 text-slate-400" />
                                <span className="max-w-[140px] truncate">{lastInteraction}</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs border-slate-200 bg-white p-3 text-slate-700 shadow-lg">
                              <div>
                                <p className="mb-1 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                                  Last Interaction
                                </p>
                                <p className="text-sm">{lastInteraction}</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-3.5 py-2.5 align-top">
                        <Badge className={`${getStatusColor(statusObj ? String(statusObj.value) : "New")} rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-none hover:bg-current/10`}>
                          {statusObj ? statusObj.value : "New"}
                        </Badge>
                      </td>
                      <td className="px-3.5 py-2.5 align-top text-[12px] text-slate-600">
                        {formatDate(lead.createdAt)}
                      </td>
                      <td className="px-3.5 py-2.5 align-top">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              className="h-7 w-7 rounded-full p-0 text-slate-500 hover:bg-slate-100"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreHorizontal className="h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="border-slate-200 bg-white">
                            <DropdownMenuItem onClick={(e) => handleUpdate(lead, e)}>
                              Update
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => handleDelete(lead.id, e)} className="text-rose-600">
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-2.5 border-t border-slate-200 px-3.5 py-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="text-[12px] text-slate-500">
            Showing {(currentPage - 1) * pageSize + (leads.length > 0 ? 1 : 0)} -{" "}
            {Math.min(currentPage * pageSize, total)} of {total} leads
          </div>

          {totalPages > 1 && (
            <Pagination className="justify-end">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => goToPage(currentPage - 1)}
                    className={currentPage === 1 ? "pointer-events-none opacity-40" : "cursor-pointer"}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </PaginationPrevious>
                </PaginationItem>

                {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                  let pageNum = index + 1;
                  if (totalPages > 5) {
                    if (currentPage <= 3) {
                      pageNum = index + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + index;
                    } else {
                      pageNum = currentPage - 2 + index;
                    }
                  }

                  return (
                    <PaginationItem key={pageNum}>
                      <PaginationLink
                        onClick={() => goToPage(pageNum)}
                        isActive={currentPage === pageNum}
                        className="cursor-pointer rounded-xl"
                      >
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}

                <PaginationItem>
                  <PaginationNext
                    onClick={() => goToPage(currentPage + 1)}
                    className={currentPage === totalPages ? "pointer-events-none opacity-40" : "cursor-pointer"}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </PaginationNext>
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </div>
      </Card>

      <UpdateLeadModal
        lead={selectedLead}
        open={showUpdateModal}
        onOpenChange={setShowUpdateModal}
        onUpdateLead={onUpdateLeads}
        rmList={rmList}
        onLeadAdded={onLeadAdded}
        statusOptions={statusOptions}
      />

      <InteractionHistoryModal
        lead={selectedLead}
        open={showInteractionModal}
        onOpenChange={setShowInteractionModal}
        statusOptions={statusOptions}
      />
    </TooltipProvider>
  );
};

export default LeadTable;
